import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FloatingActionButton, Icon } from '../../components/common';
import { PageHeader } from '../../components/layout';
import { FEATURED_CLIENTS, OTHER_CLIENTS } from '../../constants';

// Number of venues to show initially (7 rows x 3 columns = 21)
const INITIAL_VENUES_COUNT = 21;

/**
 * Clientele Page - Showcase all venue clients
 * Two sections: Featured (large cards) and Other venues (compact list)
 */
const Clientele = () => {
  const navigate = useNavigate();
  const [showAllVenues, setShowAllVenues] = useState(false);
  
  // Get venues to display based on state
  const displayedVenues = showAllVenues ? OTHER_CLIENTS : OTHER_CLIENTS.slice(0, INITIAL_VENUES_COUNT);
  const remainingCount = OTHER_CLIENTS.length - INITIAL_VENUES_COUNT;

  // Get venue type icon
  const getTypeIcon = (type) => {
    const icons = {
      mall: 'storefront',
      venue: 'location_city',
      resort: 'villa',
      hostel: 'hotel',
      hotel: 'hotel',
      restaurant: 'restaurant',
      lounge: 'nightlife',
      bar: 'sports_bar',
      cafe: 'local_cafe',
      banquet: 'celebration',
    };
    return icons[type] || 'place';
  };

  // Page-specific meta information
  const pageTitle = "Our Clientele | Matisa - Singer & Guitarist";
  const pageDescription = "140+ venues across 10+ cities. Matisa has performed at India's finest hotels, resorts, restaurants, and event venues.";

  return (
    <div className="w-full max-w-[480px] min-h-screen-safe bg-background-dark shadow-2xl overflow-hidden flex flex-col relative mx-auto">
      {/* Page Meta Tags - React 19 hoists these to <head> */}
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      {/* Open Graph / Social Media Meta Tags */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://matisa.in/clientele" />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content="https://matisa.in/hero_main.png" />
      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content="https://matisa.in/hero_main.png" />
      
      {/* Sticky Header with Menu */}
      <PageHeader title="Matisa" />

      {/* Main Content Scroll Area */}
      <main className="flex-1 overflow-y-auto scrollbar-hide">
        {/* Hero Section - Redesigned */}
        <section className="relative px-6 pt-10 pb-8">
          {/* Background Glow */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-64 bg-primary/10 rounded-full blur-3xl pointer-events-none" />
          
          <div className="relative flex flex-col items-center gap-6">
            {/* Icon Badge */}
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-gold/30 to-primary/30 rounded-full blur-xl" />
              <div className="relative w-16 h-16 rounded-full bg-gradient-to-br from-gold/20 to-primary/20 border border-gold/30 flex items-center justify-center">
                <Icon name="workspace_premium" size="lg" className="text-gold" />
              </div>
            </div>

            {/* Title */}
            <div className="flex flex-col items-center gap-2 text-center">
              <h1 className="text-white text-2xl font-bold tracking-tight">
                Our Clientele
              </h1>
              <p className="text-[#90a9cb] text-sm max-w-[280px] leading-relaxed">
                Proud to have performed at some of India's finest venues
              </p>
            </div>

            {/* Stats Row */}
            <div className="w-full grid grid-cols-3 gap-2 mt-2">
              <div className="flex flex-col items-center py-4 px-2 bg-gradient-to-b from-white/[0.08] to-transparent border border-white/10 rounded-2xl">
                <span className="text-gold text-2xl font-bold">140+</span>
                <span className="text-[#90a9cb] text-[10px] font-medium uppercase tracking-wider mt-1">Venues</span>
              </div>
              <div className="flex flex-col items-center py-4 px-2 bg-gradient-to-b from-white/[0.08] to-transparent border border-white/10 rounded-2xl">
                <span className="text-primary text-2xl font-bold">10+</span>
                <span className="text-[#90a9cb] text-[10px] font-medium uppercase tracking-wider mt-1">Cities</span>
              </div>
              <div className="flex flex-col items-center py-4 px-2 bg-gradient-to-b from-white/[0.08] to-transparent border border-white/10 rounded-2xl">
                <span className="text-white text-2xl font-bold">600+</span>
                <span className="text-[#90a9cb] text-[10px] font-medium uppercase tracking-wider mt-1">Shows</span>
              </div>
            </div>
          </div>
        </section>

        {/* Trusted By Divider */}
        <div className="px-6 pb-6">
          <div className="flex items-center gap-3">
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-gold/40 to-gold/40" />
            <span className="text-gold/80 text-[10px] font-semibold uppercase tracking-[0.25em]">Trusted By</span>
            <div className="h-px flex-1 bg-gradient-to-l from-transparent via-gold/40 to-gold/40" />
          </div>
        </div>

        {/* Featured Clients - Names You May Know */}
        <section className="flex flex-col gap-4 px-6 pb-6">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-gold/10 border border-gold/20 flex items-center justify-center">
              <Icon name="star" size="sm" className="text-gold" />
            </div>
            <h2 className="text-white text-base font-bold tracking-tight">Names You May Know</h2>
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            {FEATURED_CLIENTS.map((client) => (
              <div
                key={client.id}
                className="group relative overflow-hidden rounded-2xl border border-white/10 hover:border-white/20 bg-surface-dark/50 transition-all duration-300"
              >
                {/* Content - Vertical Layout */}
                <div className="flex flex-col p-3 gap-3">
                  {/* Logo Container - Large Square Box */}
                  <div className="w-full aspect-square rounded-xl bg-white flex items-center justify-center overflow-hidden">
                    {client.logo ? (
                      <img 
                        src={client.logo} 
                        alt={`${client.name} logo`}
                        className="w-full h-full object-contain p-4"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <span 
                      className={`text-gray-800 text-sm font-bold text-center px-2 ${client.logo ? 'hidden' : 'flex'}`}
                    >
                      {client.name}
                    </span>
                  </div>
                  
                  {/* Info */}
                  <div className="flex flex-col gap-1">
                    <h3 className="text-primary text-sm font-bold tracking-tight leading-tight">
                      {client.name}
                    </h3>
                    <div className="flex items-center gap-1">
                      <Icon name="location_on" size="xs" className="text-gold/70 flex-shrink-0" />
                      <span className="text-white text-xs truncate">{client.location}</span>
                    </div>
                    {client.venues && (
                      <span className="text-[#90a9cb]/70 text-[10px] truncate">
                        {client.venues.join(' • ')}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Divider */}
        <div className="mx-6 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />

        {/* Other Amazing Venues */}
        <section className="flex flex-col gap-4 px-6 py-6">
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
                <Icon name="auto_awesome" size="sm" className="text-primary" />
              </div>
              <h2 className="text-white text-base font-bold tracking-tight">More Amazing Venues</h2>
            </div>
            <p className="text-[#90a9cb] text-xs pl-[42px]">Restaurants, lounges, and event spaces</p>
          </div>
          
          {/* Compact Grid - 3 columns */}
          <div className="grid grid-cols-3 gap-2">
            {displayedVenues.map((client) => (
              <div
                key={client.id}
                className="group flex flex-col gap-2 p-2 rounded-xl bg-white/5 border border-white/5 hover:border-white/10 hover:bg-white/10 transition-all duration-200"
              >
                {/* Logo/Image Box */}
                <div className="w-full aspect-square rounded-lg bg-surface-highlight flex items-center justify-center overflow-hidden">
                  {client.logo ? (
                    <img 
                      src={client.logo} 
                      alt={`${client.name} logo`}
                      className="w-full h-full object-contain p-2"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div 
                    className={`flex-col items-center justify-center gap-1 ${client.logo ? 'hidden' : 'flex'}`}
                  >
                    <Icon name={getTypeIcon(client.type)} size="sm" className="text-primary/50" />
                    <span className="text-white/70 text-[9px] font-bold text-center px-1 leading-tight line-clamp-2">
                      {client.name}
                    </span>
                  </div>
                </div>
                
                {/* Name & Location */}
                <div className="flex flex-col gap-0.5">
                  <span className="text-white text-[10px] font-semibold leading-tight line-clamp-1">
                    {client.name}
                  </span>
                  <span className="text-[#90a9cb]/60 text-[9px] truncate">
                    {client.location}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Show More / Show Less Button */}
          {OTHER_CLIENTS.length > INITIAL_VENUES_COUNT && (
            <button
              onClick={() => setShowAllVenues(!showAllVenues)}
              className="w-full flex items-center justify-center gap-2 py-3 mt-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-primary text-sm font-semibold transition-all"
            >
              <Icon name={showAllVenues ? "expand_less" : "expand_more"} size="sm" />
              {showAllVenues ? "Show Less" : `Show ${remainingCount} More Venues`}
            </button>
          )}
        </section>

        {/* CTA Section */}
        <section className="px-6 pb-8">
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary/10 via-surface-dark to-gold/5 border border-white/10 p-6">
            {/* Decorative Elements */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-gold/10 rounded-full blur-2xl pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-primary/10 rounded-full blur-2xl pointer-events-none" />
            
            <div className="relative flex flex-col items-center text-center gap-5">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-gold/20 to-gold/5 flex items-center justify-center border border-gold/30">
                <Icon name="celebration" size="md" className="text-gold" />
              </div>
              
              <div className="flex flex-col gap-2">
                <h3 className="text-white text-xl font-bold">Want to be on this list?</h3>
                <p className="text-[#90a9cb] text-sm">
                  Book Matisa for your next event
                </p>
              </div>
              
              <button
                onClick={() => {
                  const scrollContainer = document.querySelector('.overflow-y-auto');
                  if (scrollContainer) scrollContainer.scrollTo(0, 0);
                  navigate('/enquiry');
                }}
                className="flex items-center gap-2 px-8 py-3.5 bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 text-white text-sm font-bold rounded-xl transition-all duration-300 shadow-lg shadow-primary/20"
              >
                <Icon name="event" size="sm" />
                Book Now
              </button>
            </div>
          </div>
        </section>

        {/* Back Button */}
        <section className="px-6 pb-24">
          <button
            onClick={() => {
              const scrollContainer = document.querySelector('.overflow-y-auto');
              if (scrollContainer) scrollContainer.scrollTo(0, 0);
              navigate('/shows');
            }}
            className="w-full flex items-center justify-center gap-2 py-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-[#90a9cb] text-sm font-medium transition-all"
          >
            <Icon name="arrow_back" size="sm" />
            Explore What I Do
          </button>
        </section>
      </main>

      {/* Floating Action Button */}
      <FloatingActionButton to="/enquiry" />

      {/* Bottom Blur Overlay */}
      <div className="pointer-events-none fixed bottom-0 left-0 h-12 w-full bg-gradient-to-t from-background-dark to-transparent lg:absolute" />
    </div>
  );
};

export default Clientele;
